import _ from 'lodash';

export class StringUtilities {

    static REGEX_NON_WORDS: RegExp = /[^\w]+/g;
    static REGEX_SPECIAL_CHARACTERS: RegExp = /[.*+?^${}()|[\]\\]/g;

    /**
     * Checks if a passed in value is null or undefined or a blank object.
     * 
     * @param value The value to verify.
     */
    static isBlank(value: any): boolean {
        return _.isEmpty(value) && !_.isNumber(value) || _.isNaN(value);
    }

    static toBase64String(value: string): string {
        return this.isBlank(value) ? null : window.btoa(value);
    }

    static fromBase64String(value: string): string {
        return this.isBlank(value) ? null : window.atob(value);
    }

    static IsJsonString(str) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return !this.isBlank(str);
    }

    static TryParseJsonString<T>(str: string): T {
        try {
            return JSON.parse(str) as T;
        } catch (e) {
            return null;
        }
    }

    static areEqualIgnoreCase(value: string, value2: string) {
        return value.localeCompare(value2, undefined, { sensitivity: 'accent' }) === 0;
    }

    /**
     * remove trailing slash '/' if found, only remove one character
     * @param value string to trim
     * @returns trimmed string without trailing slash
     */
    static trimTrailingSlash(value: string): string {
        return this.trimTrailingCharacter(value, '/');
    }

    static trimTrailingCharacter(value: string, character: string): string {
        return value.charAt(value.length - 1) === character ? value.slice(0, -1) : value;
    }

    /**
     * Converts string to regular expression pattern string.
     * @param str
     */
    static toUriEncodedString(str: string): string {
        return encodeURIComponent(str.replace(StringUtilities.REGEX_NON_WORDS, "-"));
    }

    /**
     * Converts string to regular expression pattern string.
     * Escapes regex special chars properly, eg. 'abc.123' -> 'abc\.123',
     * in order to use the pattern in a RegExp constructor.
     * @param str
     */
    static toRegExpString(str: string): string {
        // escape regex special characters & string escape
        return str.replace(StringUtilities.REGEX_SPECIAL_CHARACTERS, '\\$&');
    }

    static snakeToPascal(str: string): string {
        return _.upperFirst(_.camelCase(str));
    }

    static findKeyIgnoreCase(obj, searchKey): string {
        return Object.keys(obj).find(key => key.toLowerCase() === searchKey.toLowerCase());
    }

    /**
     * Converts a string from snake_case, kebab-case, or PascalCase to camelCase.
     * @param str The string to convert
     * @returns The camelCase version of the string
     */
    static toCamelCase(str: string): string {
        return str
            // Handle snake_case and kebab-case
            .replace(/[-_]+(.)?/g, (_, char) => char ? char.toUpperCase() : '')
            // Handle PascalCase (first letter lowercase)
            .replace(/^[A-Z]/, char => char.toLowerCase());
    }

    /**
     * Returns the directory portion of a file path, excluding the filename, with a leading slash.
     * e.g. "basespace/file.txt" with name "file.txt" -> "/basespace/"
     *      "file.txt" with name "file.txt" -> "/"
     */
    static getDirectoryPath(path: string, fileName: string): string {
        let dir = path || '';
        if (fileName) {
            const idx = dir.lastIndexOf(fileName);
            if (idx > -1) {
                dir = dir.substring(0, idx);
            }
        }
        return '/' + dir;
    }

    /**
     * Recursively converts all keys in an object from any case to camelCase.
     * Handles nested objects and arrays.
     * @param obj The object to convert
     * @returns A new object with all keys in camelCase
     */
    static deepCamelCase(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        // Handle arrays
        if (Array.isArray(obj)) {
            return obj.map(item => StringUtilities.deepCamelCase(item));
        }

        // Handle primitive types
        if (typeof obj !== 'object') {
            return obj;
        }

        // Handle objects
        const result: any = {};

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                // Convert key to camelCase
                const camelKey = StringUtilities.toCamelCase(key);

                // Recursively process the value
                result[camelKey] = StringUtilities.deepCamelCase(obj[key]);
            }
        }

        return result;
    }
}

